import { Button, ButtonGroup, ProgressIndicator } from '@veneer/core';
import type { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import HeaderBar from '../../components/HeaderBar';
import ModalError, { ModalErrorDetail } from '../../components/Modals/Error';
import Preview from '../../components/Preview';
import { ScreenInfo, useDataCollection } from '../../jarvis/dataCollection';
import InputsInfoBar from '../../components/InputsInfoBar';
import './index.css';

import type { CartData, Project } from '../../types';
import { usePhotobookApiWebHttpClient } from '../../helpers/useJarvisApiWebHttpClient';
import { PhotobookApi } from '../../helpers/apis/photobookApi';
import { LocalStorageHelper } from '../../helpers/localStorage';

const CartPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeProductCategory, setActiveProductCategory] = useState('');
  const [projectData, setProjectData] = useState({} as Project);
  const [activeProjectId, setActiveProjectId] = useState('');
  const [userMediaclipToken, setUserMediaclipToken] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<ModalErrorDetail>();
  const dataCollection = useDataCollection();
  const [isTester, setIsTester] = useState(false);
  const [testFulfiller, setTestFulfiller] = useState(false);
  const [ecommerceEnabled, setEcommerceEnabled] = useState(false);

  const history = useHistory();

  const photobookApi = new PhotobookApi(usePhotobookApiWebHttpClient());

  const screenInfo: ScreenInfo = { name: 'Cart', activity: 'OrderPhotobook-v01' };
  const checkoutButtonName = 'CheckoutButton';
  const backButtonName = 'BackButton';

  const sendClickEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: activeProjectId }, screenInfo);
  };

  useEffect(() => {
    LocalStorageHelper.setLastPage('cart');
    obtainCartItems();
    dataCollection.sendScreenViewEvent(screenInfo);
  }, [dataCollection]);

  const obtainCartItems = async () => {
    try {
      const response: AxiosResponse = await photobookApi.getCart();
      if (!response.data) throw new Error('Unable to load response');
      handleCartData(response.data);
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      setErrorDetail({
        context: 'Error obtaining cart items',
        ...(error instanceof Error && { error: error }),
      });
      console.log(error);
    }
  };

  const handleCartData = (data: CartData) => {
    const {
      userMediaclipToken: returnedUserMediaclipToken,
      projects,
      isTester: returnedIsTester,
      testFulfiller: returnedTestFulfiller,
      ecommerceEnabled: returnedEcommerceEnabled,
    } = data;
    if (projects.length == 0) {
      return displayEmptyCartModal();
    }
    const { id: returnedProjectId, productCategory = 'photobook' } = projects[0];
    if (projects[0].price === '$0.00' || projects[0].price === '$0,00') {
      projects[0].price = 'FREE';
    }
    setProjectData(projects[0]);
    setUserMediaclipToken(returnedUserMediaclipToken);
    setActiveProjectId(returnedProjectId);
    setActiveProductCategory(productCategory.toLowerCase());
    setIsTester(returnedIsTester ? true : false);
    setTestFulfiller(returnedTestFulfiller ? true : false);
    setEcommerceEnabled(returnedEcommerceEnabled ? true : false);
    setIsLoading(false);
  };

  const displayEmptyCartModal = () => {
    setIsLoading(false);
    setHasError(true);
    setErrorDetail({ context: 'Cart is empty', errorMessage: 'Your cart is empty.' });
    console.log('Empty Cart');
  };

  const onClickCheckout = () => {
    sendClickEvent(checkoutButtonName);
    history.push('/checkout');
  };

  const onClickBack = () => {
    sendClickEvent(backButtonName);
    history.push(`/designer?projectId=${activeProjectId}&resume=false`);
  };

  if (hasError) {
    return <ModalError state={hasError} detail={errorDetail as ModalErrorDetail} screenInfo={screenInfo} />;
  }

  if (isLoading) {
    return (
      <div className="spinner">
        <ProgressIndicator />
      </div>
    );
  }

  return (
    <>
      <HeaderBar handleBack={onClickBack} title="Your Cart" />
      <section className="CartPage">
        <div className={`imageContainer ${activeProductCategory}`}>
          <Preview
            projectId={activeProjectId}
            userMediaclipToken={userMediaclipToken}
            category={activeProductCategory}
          />
        </div>
        <div className="cartInfo">
          <div className="cart">
            <div className="cartItem">
              <div className="itemName">
                <span className="title">{projectData.productFriendlyName}</span>
                <span className="price">{projectData.price}</span>
              </div>
              <span className="subtitle-regular link" onClick={onClickBack}>
                Edit
              </span>
            </div>
          </div>
          <div className="totalPriceInfo">
            <div className="priceItem total">
              <span className="name">
                <b>Subtotal</b>
              </span>
              <span className="value">
                <b>{projectData.price}</b>
              </span>
            </div>
          </div>
        </div>
        <div className="cartButtons">
          <ButtonGroup orientation="horizontal">
            <Button appearance="secondary" onClick={onClickBack}>
              Back
            </Button>
            <Button onClick={onClickCheckout}>Checkout</Button>
          </ButtonGroup>
          {isTester && (
            <div className="debug-disclaimer caption-small">
              <p>* You are configured as a Tester.</p>
              {testFulfiller ? <p>order will not print</p> : <p>ORDER WILL PRINT</p>}
              {ecommerceEnabled ? <p>YOUR CARD WILL BE CHARGED</p> : <p>your card will not be charged</p>}
            </div>
          )}
        </div>
      </section>
      {isTester && <InputsInfoBar />}
    </>
  );
};

export default CartPage;
